"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  projectileSteps,
  TrajectoryGraph,
} from "@/components/tools/physics-mechanics/projectile-parts";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import { Working } from "@/components/tools/physics-mechanics/working";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  formatSig,
  SIG_FIG_CHOICES,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  GRAVITY_PRESETS,
  type ProjectileInput,
  type ProjectileResult,
  positionAt,
  solveProjectile,
} from "@/lib/tool-kit/physics-mechanics/projectile";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  ACCELERATION,
  ANGLE,
  fromBase,
  LENGTH,
  SPEED,
  toBase,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

/** English strings. A translated content file overrides these through `ui`. */
export const PROJECTILE_MOTION_CALCULATOR_STRINGS = {
  instructions:
    "Enter the launch speed, angle and height. The projectile moves under gravity alone — no air resistance — and lands on level ground at height zero.",
  speed: "Launch speed v₀",
  angle: "Launch angle θ above the horizontal",
  angleHint:
    "Use a negative angle to throw downward (only useful from a height).",
  height: "Launch height h₀ above the ground",
  heightHint: "Leave blank or enter 0 for a launch from ground level.",
  gravity: "Gravity",
  gravityEarth: "Earth (9.80665 m/s², standard gravity)",
  gravityMoon: "Moon (1.62 m/s²)",
  gravityMars: "Mars (3.73 m/s², mean)",
  gravityCustom: "Custom value",
  customGravity: "Gravitational acceleration g",
  unitFor: "Unit for {name}",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  range: "Range (horizontal distance)",
  timeOfFlight: "Time of flight",
  maxHeight: "Maximum height above the ground",
  impactSpeed: "Speed at impact",
  impactAngle: "Angle at impact, below horizontal",
  bestAngle: "Angle for the longest range",
  bestRange: "Longest possible range at this speed",
  second: "s",
  copy: "Copy results",
  copied: "Copied!",
  copyText:
    "Range {range}; time of flight {time}; maximum height {top}; impact speed {speed} at {angle} below horizontal",
  workingHeading: "The working",
  stepInputs:
    "In SI units and degrees: v₀ = {v} m/s, θ = {angle}°, h₀ = {h} m, g = {g} m/s².",
  stepComponents:
    "Split the launch velocity: vx = v₀ cos θ = {v} × cos {angle}° = {vx} m/s, and vy = v₀ sin θ = {v} × sin {angle}° = {vy} m/s.",
  stepFlight:
    "The flight ends when the height y = h₀ + vy·t − ½gt² reaches 0: t = (vy + √(vy² + 2gh₀)) / g = ({vy} + √({vy}² + 2 × {g} × {h})) / {g} = {time} s.",
  stepRange: "Range: R = vx × t = {vx} × {time} = {range} m.",
  stepPeak:
    "Peak: it is highest when vy has fallen to 0, at t = vy / g = {vy} / {g} = {time} s, where H = h₀ + vy² / (2g) = {h} + {vy}² / (2 × {g}) = {top} m.",
  stepNoPeak:
    "The projectile is not launched upward, so its highest point is where it starts: {h} m.",
  stepImpact:
    "At impact vy = vy − g·t = {vy} − {g} × {time} = {vyf} m/s, so the speed is √(vx² + vy²) = √({vx}² + {vyf}²) = {speed} m/s, at {angle}° below the horizontal.",
  tableHeading: "Position during the flight",
  tableTime: "Time ({unit})",
  tableX: "Horizontal distance x ({unit})",
  tableY: "Height y ({unit})",
  graphHeading: "Trajectory",
  graphTitle:
    "Trajectory of the projectile: it travels {range} horizontally and reaches {height} at its highest point",
  xAxis: "Horizontal distance x ({unit})",
  yAxis: "Height y ({unit})",
  launchPoint: "Launch",
  peakPoint: "Peak {value}",
  landingPoint: "Lands at {value}",
  graphNote:
    "Both axes use the same scale, so the curve has its true shape. The path is a parabola.",
  downloadPng: "Download graph (PNG)",
  assumptions:
    "Assumes no air resistance, a constant g, a point-like object and a flat landing level at height zero. Real projectiles — especially fast, light or tumbling ones — land shorter than this ideal model predicts.",
  enterPrompt: "Enter the launch speed and angle to see the flight.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign.",
  errors: {
    "negative-speed":
      "Launch speed cannot be negative. The angle sets the direction of the throw.",
    "negative-height":
      "Launch height must be zero or more: it is measured above the landing level.",
    "bad-gravity": "g must be greater than zero.",
    "bad-angle":
      "The launch angle must be between −90° and 90° (−π/2 and π/2 radians).",
    "no-flight":
      "From ground level a launch at 0° or below never leaves the ground. Raise the angle, or enter a launch height above zero.",
  },
} as const;

type Strings = typeof PROJECTILE_MOTION_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type GravityChoice = "earth" | "moon" | "mars" | "custom";

const TABLE_ROWS = 5;

interface Parsed {
  input: ProjectileInput | null;
  unreadable: "speed" | "angle" | "height" | "customGravity" | null;
  missing: boolean;
}

interface FormState {
  speed: string;
  speedUnit: string;
  angle: string;
  angleUnit: string;
  height: string;
  heightUnit: string;
  gravity: GravityChoice;
  customG: string;
  customGUnit: string;
}

const INITIAL: FormState = {
  speed: "20",
  speedUnit: "mps",
  angle: "45",
  angleUnit: "deg",
  height: "0",
  heightUnit: "m",
  gravity: "earth",
  customG: "9.8",
  customGUnit: "mps2",
};

function parseForm(form: FormState): Parsed {
  const speed = parseQuantity(form.speed);
  const angle = parseQuantity(form.angle);
  const height = form.height.trim() === "" ? 0 : parseQuantity(form.height);
  const custom = parseQuantity(form.customG);
  const empty = (text: string) => text.trim() === "";
  if (empty(form.speed) || empty(form.angle)) {
    return { input: null, unreadable: null, missing: true };
  }
  if (speed === null) {
    return { input: null, unreadable: "speed", missing: false };
  }
  if (angle === null) {
    return { input: null, unreadable: "angle", missing: false };
  }
  if (height === null) {
    return { input: null, unreadable: "height", missing: false };
  }
  if (form.gravity === "custom" && custom === null) {
    return { input: null, unreadable: "customGravity", missing: false };
  }
  const gravity =
    form.gravity === "custom"
      ? toBase(custom ?? 0, ACCELERATION, form.customGUnit)
      : GRAVITY_PRESETS[form.gravity];
  return {
    input: {
      speed: toBase(speed, SPEED, form.speedUnit),
      angle: toBase(angle, ANGLE, form.angleUnit),
      height: toBase(height, LENGTH, form.heightUnit),
      gravity,
    },
    unreadable: null,
    missing: false,
  };
}

function Stats({
  input,
  result,
  form,
  strings,
  fmt,
  sig,
}: {
  input: ProjectileInput;
  result: ProjectileResult;
  form: FormState;
  strings: Strings;
  fmt: Fmt;
  sig: number;
}) {
  const lengthUnit = unitSymbol(LENGTH, form.heightUnit);
  const speedUnit = unitSymbol(SPEED, form.speedUnit);
  const angleUnit = unitSymbol(ANGLE, form.angleUnit);
  const len = (metres: number) =>
    `${formatSig(fmt, fromBase(metres, LENGTH, form.heightUnit), sig)} ${lengthUnit}`;
  const spd = (value: number) =>
    `${formatSig(fmt, fromBase(value, SPEED, form.speedUnit), sig)} ${speedUnit}`;
  const ang = (radians: number) =>
    `${formatSig(fmt, fromBase(radians, ANGLE, form.angleUnit), sig)}${form.angleUnit === "deg" ? "" : " "}${angleUnit}`;
  const time = `${formatSig(fmt, result.timeOfFlight, sig)} ${strings.second}`;
  const copy = t(strings.copyText, {
    range: len(result.range),
    time,
    top: len(result.maxHeight),
    speed: spd(result.impactSpeed),
    angle: ang(result.impactAngle),
  });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.range} value={len(result.range)} />
        <StatCard emphasis label={strings.timeOfFlight} value={time} />
        <StatCard
          emphasis
          label={strings.maxHeight}
          value={len(result.maxHeight)}
        />
        <StatCard label={strings.impactSpeed} value={spd(result.impactSpeed)} />
        <StatCard label={strings.impactAngle} value={ang(result.impactAngle)} />
        <StatCard label={strings.bestAngle} value={ang(result.bestAngle)} />
      </StatGrid>
      <p className={HINT}>
        {strings.bestRange}: {len(result.bestRange)}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={copy}
      />
      <Working
        heading={strings.workingHeading}
        steps={projectileSteps(input, result, strings, fmt, sig)}
      />
    </>
  );
}

function PositionTable({
  input,
  result,
  form,
  strings,
  fmt,
  sig,
}: {
  input: ProjectileInput;
  result: ProjectileResult;
  form: FormState;
  strings: Strings;
  fmt: Fmt;
  sig: number;
}) {
  const lengthUnit = unitSymbol(LENGTH, form.heightUnit);
  const rows: string[][] = [];
  for (let i = 0; i <= TABLE_ROWS; i += 1) {
    const point = positionAt(
      input,
      result,
      (result.timeOfFlight * i) / TABLE_ROWS
    );
    rows.push([
      formatSig(fmt, point.t, sig),
      formatSig(fmt, fromBase(point.x, LENGTH, form.heightUnit), sig),
      formatSig(fmt, fromBase(point.y, LENGTH, form.heightUnit), sig),
    ]);
  }
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          t(strings.tableTime, { unit: strings.second }),
          t(strings.tableX, { unit: lengthUnit }),
          t(strings.tableY, { unit: lengthUnit }),
        ]}
        rows={rows}
      />
    </div>
  );
}

export default function ProjectileMotionCalculatorWidget() {
  const strings = useToolUi(PROJECTILE_MOTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [sigFigs, setSigFigs] = useState("4");
  const sig = Number(sigFigs);
  const patch = (change: Partial<FormState>) => setForm({ ...form, ...change });
  const parsed = parseForm(form);
  const result = parsed.input ? solveProjectile(parsed.input) : null;

  return (
    <>
      <p className={HINT}>{strings.instructions}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          id="pm-speed"
          label={strings.speed}
          onUnit={(speedUnit) => patch({ speedUnit })}
          onValue={(speed) => patch({ speed })}
          unit={form.speedUnit}
          unitLabel={t(strings.unitFor, { name: strings.speed })}
          units={unitOptions(SPEED)}
          value={form.speed}
        />
        <QuantityField
          hint={strings.angleHint}
          id="pm-angle"
          label={strings.angle}
          onUnit={(angleUnit) => patch({ angleUnit })}
          onValue={(angle) => patch({ angle })}
          unit={form.angleUnit}
          unitLabel={t(strings.unitFor, { name: strings.angle })}
          units={unitOptions(ANGLE)}
          value={form.angle}
        />
        <QuantityField
          hint={strings.heightHint}
          id="pm-height"
          label={strings.height}
          onUnit={(heightUnit) => patch({ heightUnit })}
          onValue={(height) => patch({ height })}
          unit={form.heightUnit}
          unitLabel={t(strings.unitFor, { name: strings.height })}
          units={unitOptions(LENGTH)}
          value={form.height}
        />
        <SelectField
          id="pm-gravity"
          label={strings.gravity}
          onChange={(gravity) => patch({ gravity })}
          options={[
            { value: "earth", label: strings.gravityEarth },
            { value: "moon", label: strings.gravityMoon },
            { value: "mars", label: strings.gravityMars },
            { value: "custom", label: strings.gravityCustom },
          ]}
          value={form.gravity}
        />
        {form.gravity === "custom" ? (
          <QuantityField
            id="pm-custom-g"
            label={strings.customGravity}
            onUnit={(customGUnit) => patch({ customGUnit })}
            onValue={(customG) => patch({ customG })}
            unit={form.customGUnit}
            unitLabel={t(strings.unitFor, { name: strings.customGravity })}
            units={unitOptions(ACCELERATION)}
            value={form.customG}
          />
        ) : null}
        <SelectField
          id="pm-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {parsed.unreadable === null ? null : (
          <ErrorNotice>
            {t(strings.unreadable, { name: strings[parsed.unreadable] })}
          </ErrorNotice>
        )}
        {parsed.missing ? <Notice>{strings.enterPrompt}</Notice> : null}
        {result !== null && "error" in result ? (
          <ErrorNotice>{strings.errors[result.error]}</ErrorNotice>
        ) : null}
        {parsed.input !== null && result !== null && !("error" in result) ? (
          <>
            <Stats
              fmt={fmt}
              form={form}
              input={parsed.input}
              result={result}
              sig={sig}
              strings={strings}
            />
            <PositionTable
              fmt={fmt}
              form={form}
              input={parsed.input}
              result={result}
              sig={sig}
              strings={strings}
            />
            <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
            <TrajectoryGraph
              fmt={fmt}
              input={parsed.input}
              lengthFactor={toBase(1, LENGTH, form.heightUnit)}
              lengthUnit={unitSymbol(LENGTH, form.heightUnit)}
              result={result}
              sig={sig}
              strings={strings}
            />
            <p className={HINT}>{strings.assumptions}</p>
          </>
        ) : null}
      </section>
    </>
  );
}
